import React, { useEffect, useState, useContext, useRef } from 'react';
import styled from 'styled-components';
import { MobileAndTabletHeader, DesktopHeader, Footer } from 'components';
import { LocalContext } from 'context';
import { useStaticQuery, graphql } from 'gatsby';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation } from '@reach/router';
import { useGSAP } from '@gsap/react';
import { initFadeInAnimation } from 'utils';
import { GlobalStyles } from '../../styles';

function Layout({ children }) {
  const { pathname } = useLocation();
  const { userHasScrolled, showMobileMenu, initialRender } = useContext(LocalContext);
  const [showWebsiteOverlay, setShowWebsiteOverlay] = useState(false);
  const timeoutRef = useRef(null);
  const listenerAddedRef = useRef(false);
  const userHasEverCompletedConsentRef = useRef(false);

  useEffect(() => {
    if (
      !showWebsiteOverlay &&
      userHasScrolled &&
      !listenerAddedRef.current &&
      !userHasEverCompletedConsentRef.current
    ) {
      let retryCount = 0;
      const maxRetries = 200; // 10 seconds max wait time

      (function waitForCookieYes() {
        if (window.getCkyConsent) {
          const { isUserActionCompleted } = window.getCkyConsent();
          if (isUserActionCompleted) {
            userHasEverCompletedConsentRef.current = true;
          } else {
            setShowWebsiteOverlay(true);
            listenerAddedRef.current = true;
          }
        } else if (retryCount < maxRetries) {
          retryCount += 1;
          timeoutRef.current = setTimeout(waitForCookieYes, 50);
        }
      })();
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [userHasScrolled]);

  useEffect(() => {
    if (showWebsiteOverlay) {
      const pollInterval = setInterval(() => {
        if (window.getCkyConsent) {
          const { isUserActionCompleted } = window.getCkyConsent();
          if (isUserActionCompleted) {
            setShowWebsiteOverlay(false);
            userHasEverCompletedConsentRef.current = true;
            clearInterval(pollInterval);
          }
        }
      }, 100);

      return () => clearInterval(pollInterval);
    }
  }, [showWebsiteOverlay]);

  useGSAP(() => initFadeInAnimation(), { dependencies: [pathname] });

  const { stairCollections, doorCollections } = useStaticQuery(graphql`
    query {
      stairCollections: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/stairs/collections/" } }
      ) {
        nodes {
          frontmatter {
            title
            slug
          }
        }
      }
      doorCollections: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/doors/collections/" } }
      ) {
        nodes {
          frontmatter {
            title
          }
        }
      }
    }
  `);

  return (
    <>
      <GlobalStyles />
      <MobileAndTabletHeader
        stairCollections={stairCollections}
        doorCollections={doorCollections}
      />
      <DesktopHeader
        stairCollections={stairCollections}
        doorCollections={doorCollections}
        userHasScrolled={userHasScrolled}
      />
      <div style={{ position: showMobileMenu ? 'fixed' : 'relative', height: '100%' }}>
        {initialRender ? (
          <Main custom={initialRender} key={pathname} style={{ height: '100%' }}>
            {children}
          </Main>
        ) : (
          <AnimatePresence mode="popLayout">
            {pathname && (
              <Main custom={initialRender} key={pathname} style={{ height: '100%' }}>
                {children}
              </Main>
            )}
          </AnimatePresence>
        )}
        <Footer />
      </div>
      {process.env.NODE_ENV === 'production' && (
        <AnimatePresence>{showWebsiteOverlay && <WebsiteOverlay />}</AnimatePresence>
      )}
    </>
  );
}

const Main = styled(motion.main).attrs({
  initial: (initialRender) => ({
    opacity: initialRender ? 1 : 0
  }),
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: {
    type: 'spring',
    mass: 0.35,
    stiffness: 75,
    duration: 500
  }
})`
  background-color: var(--connolly-grey);
  max-width: 100vw;
  position: relative;
`;

const WebsiteOverlay = styled(motion.div).attrs({
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  },
  exit: {
    opacity: 0
  },
  transition: {
    duration: 0.5
  }
})`
  background-color: rgb(000 000 000 / 52.5%);
  height: 100%;
  left: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 7;
`;

export default Layout;
